import React, { createContext, useContext, useState, useEffect, Suspense, lazy } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Lazy-loaded Pages for code splitting (reduces initial bundle size)
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const DiscoverPage = lazy(() => import("@/pages/DiscoverPage"));
const TrackDetailPage = lazy(() => import("@/pages/TrackDetailPage"));
const UploadPage = lazy(() => import("@/pages/UploadPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const SubscriptionPage = lazy(() => import("@/pages/SubscriptionPage"));
const PlaylistsPage = lazy(() => import("@/pages/PlaylistsPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const InboxPage = lazy(() => import("@/pages/InboxPage"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const EmbedPage = lazy(() => import("@/pages/EmbedPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const EditTrackPage = lazy(() => import("@/pages/EditTrackPage"));
const PayoutSettingsPage = lazy(() => import("@/pages/PayoutSettingsPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const AlbumStorePage = lazy(() => import("@/pages/AlbumStorePage"));
const CreateAlbumPage = lazy(() => import("@/pages/CreateAlbumPage"));
const AlbumDetailPage = lazy(() => import("@/pages/AlbumDetailPage"));
const ContentHubPage = lazy(() => import("@/pages/ContentHubPage"));
const SeriesDetailPage = lazy(() => import("@/pages/SeriesDetailPage"));
const PlaylistDetailPage = lazy(() => import("@/pages/PlaylistDetailPage"));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen bg-[#030303] flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-[#7000FF] border-t-transparent rounded-full animate-spin"></div>
      <p className="text-white/60 text-sm">Loading...</p>
    </div>
  </div>
);

// Components
import Navbar from "@/components/Navbar";
import MusicPlayer from "@/components/MusicPlayer";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import InstallPrompt from "@/components/InstallPrompt";
import WhatsNewModal from "@/components/WhatsNewModal";

// Context
export const AuthContext = createContext(null);
export const PlayerContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const fetchUserData = async (authToken) => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      } else {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        return null;
      }
    } catch (error) {
      console.error('Auth error:', error);
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        await fetchUserData(token);
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      return await fetchUserData(token);
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, isAuthenticated: !!user, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Player Provider
export const PlayerProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [volume, setVolume] = useState(0.8);
  const [isGuestLimited, setIsGuestLimited] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState('off'); // 'off', 'all', 'one'

  // Load history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('1samdigital_listening_history');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error('Error loading history:', e);
      }
    }
  }, []);

  // Save history to localStorage when it changes
  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem('1samdigital_listening_history', JSON.stringify(history.slice(0, 100)));
    }
  }, [history]);

  const playTrack = (track) => {
    setCurrentTrack(track);
    setIsPlaying(true);
    // Add to history
    setHistory(prev => {
      const filtered = prev.filter(t => t.id !== track.id);
      return [{ ...track, playedAt: new Date().toISOString() }, ...filtered].slice(0, 100);
    });
  };

  const pauseTrack = () => {
    setIsPlaying(false);
  };

  const resumeTrack = () => {
    setIsPlaying(true);
  };

  const addToQueue = (track) => {
    setQueue(prev => [...prev, track]);
  };

  const removeFromQueue = (index) => {
    setQueue(prev => prev.filter((_, i) => i !== index));
  };

  const clearQueue = () => {
    setQueue([]);
  };

  const nextTrack = () => {
    if (repeat === 'one') {
      // Replay current track
      setIsPlaying(true);
      return 'repeat';
    }
    
    if (queue.length > 0) {
      let nextIndex = 0;
      if (shuffle) {
        nextIndex = Math.floor(Math.random() * queue.length);
      }
      const next = queue[nextIndex];
      setQueue(prev => prev.filter((_, i) => i !== nextIndex));
      playTrack(next);
      return 'next';
    }
    
    if (repeat === 'all' && history.length > 1) {
      // Loop back to first track in history
      const firstTrack = history[history.length - 1];
      playTrack(firstTrack);
      return 'loop';
    }
    
    return 'end';
  };

  const previousTrack = () => {
    if (history.length > 1) {
      const prevTrack = history[1]; // Current is at 0, previous is at 1
      playTrack(prevTrack);
    }
  };

  const toggleShuffle = () => {
    setShuffle(prev => !prev);
  };

  const toggleRepeat = () => {
    setRepeat(prev => {
      if (prev === 'off') return 'all';
      if (prev === 'all') return 'one';
      return 'off';
    });
  };

  return (
    <PlayerContext.Provider value={{
      currentTrack,
      isPlaying,
      queue,
      history,
      volume,
      shuffle,
      repeat,
      isGuestLimited,
      playTrack,
      pauseTrack,
      resumeTrack,
      addToQueue,
      removeFromQueue,
      clearQueue,
      nextTrack,
      previousTrack,
      setVolume,
      toggleShuffle,
      toggleRepeat,
      setIsGuestLimited
    }}>
      {children}
    </PlayerContext.Provider>
  );
};

// Hooks
export const useAuth = () => useContext(AuthContext);
export const usePlayer = () => useContext(PlayerContext);

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030303]">
        <div className="w-12 h-12 border-4 border-[#7000FF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Creator Route
const CreatorRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030303]">
        <div className="w-12 h-12 border-4 border-[#7000FF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user?.is_creator) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Show the Synthesys AI spokesperson widget on the landing page only
const SynthesysGate = () => {
  const location = useLocation();

  useEffect(() => {
    const onLanding = location.pathname === '/';
    const apply = () => {
      document.querySelectorAll('iframe[src*="synthesys"]').forEach((frame) => {
        frame.style.display = onLanding ? '' : 'none';
      });
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [location.pathname]);

  return null;
};

// Layout component for pages with navbar/player
const MainLayout = ({ children }) => (
  <div className="App min-h-screen bg-[#030303]">
    <Navbar />
    <EmailVerificationBanner />
    {children}
    <MusicPlayer />
    <InstallPrompt />
    <WhatsNewModal />
    <Toaster position="bottom-right" offset={100} />
  </div>
);

// Wrapper to apply main layout
const WithLayout = ({ element }) => <MainLayout>{element}</MainLayout>;

function App() {
  return (
    <AuthProvider>
      <PlayerProvider>
        <BrowserRouter>
          <SynthesysGate />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Embed Route - Standalone without navbar/player */}
              <Route path="/embed/:trackId" element={<EmbedPage />} />
              
              {/* Public Routes */}
              <Route path="/" element={<WithLayout element={<LandingPage />} />} />
              <Route path="/login" element={<WithLayout element={<LoginPage />} />} />
              <Route path="/register" element={<WithLayout element={<RegisterPage />} />} />
              <Route path="/discover" element={<WithLayout element={<DiscoverPage />} />} />
              <Route path="/track/:trackId" element={<WithLayout element={<TrackDetailPage />} />} />
              <Route path="/track/:trackId/edit" element={<WithLayout element={<EditTrackPage />} />} />
              <Route path="/profile/:userId" element={<WithLayout element={<ProfilePage />} />} />
              <Route path="/terms" element={<WithLayout element={<TermsPage />} />} />
              <Route path="/privacy" element={<WithLayout element={<PrivacyPage />} />} />
              <Route path="/pricing" element={<WithLayout element={<PricingPage />} />} />
              <Route path="/albums" element={<WithLayout element={<AlbumStorePage />} />} />
              <Route path="/albums/:albumId" element={<WithLayout element={<AlbumDetailPage />} />} />
              <Route path="/podcasts" element={<WithLayout element={<ContentHubPage contentType="podcast" />} />} />
              <Route path="/audiobooks" element={<WithLayout element={<ContentHubPage contentType="audiobook" />} />} />
              <Route path="/series/:contentType/:creatorId/:seriesName" element={<WithLayout element={<SeriesDetailPage />} />} />
              <Route path="/playlists/:playlistId" element={<WithLayout element={<PlaylistDetailPage />} />} />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <WithLayout element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            } />
            <Route path="/playlists" element={
              <WithLayout element={<ProtectedRoute><PlaylistsPage /></ProtectedRoute>} />
            } />
            <Route path="/subscription" element={
              <WithLayout element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
            } />
            <Route path="/settings" element={
              <WithLayout element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            } />
            <Route path="/inbox" element={
              <WithLayout element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
            } />
            <Route path="/history" element={
              <WithLayout element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
            } />
            
            {/* Creator Routes */}
            <Route path="/upload" element={
              <WithLayout element={<ProtectedRoute><CreatorRoute><UploadPage /></CreatorRoute></ProtectedRoute>} />
            } />
            <Route path="/analytics" element={
              <WithLayout element={<ProtectedRoute><CreatorRoute><AnalyticsPage /></CreatorRoute></ProtectedRoute>} />
            } />
            <Route path="/payouts" element={
              <WithLayout element={<ProtectedRoute><CreatorRoute><PayoutSettingsPage /></CreatorRoute></ProtectedRoute>} />
            } />
            <Route path="/albums/create" element={
              <WithLayout element={<ProtectedRoute><CreatorRoute><CreateAlbumPage /></CreatorRoute></ProtectedRoute>} />
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <WithLayout element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            } />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </PlayerProvider>
    </AuthProvider>
  );
}

export default App;
